import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

interface InfiniteSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  onScrollEnd?: () => void;
  isLoading?: boolean;
  hasMore?: boolean;
  error?: boolean;
  placeholder?: string;
  className?: string;
  name?: string;
}

export function InfiniteSelect({
  value,
  onChange,
  options,
  onScrollEnd,
  isLoading = false,
  hasMore = true,
  error = false,
  placeholder = "Select...",
  className,
  name,
}: InfiniteSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [search, setSearch] = useState("");

  // Scroll handler
  const handleScroll = () => {
    if (!optionsContainerRef.current || !onScrollEnd || !hasMore || isLoading)
      return;

    const container = optionsContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when 50px from bottom
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (isNearBottom && hasMore && !isLoading) {
      onScrollEnd();
    }
  };

  // Filter options based on search
  useEffect(() => {
    if (search.trim() === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [options, search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (selectedValue: string) => {
    // Call onChange if it exists
    if (onChange) {
      onChange(selectedValue);
    }
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Hidden input for form submission */}
      {name && <input type="hidden" name={name} value={value || ""} />}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background text-gray-900",
          "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-destructive focus:ring-destructive",
          className
        )}
      >
        <span className={cn(!value && "text-muted-foreground", "truncate")}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 opacity-50 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 bg-white">
          {/* Search Input */}
          <div className="border-b p-2 sticky top-0 bg-popover z-10">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors text-gray-900 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          {/* Options List */}
          <div
            ref={optionsContainerRef}
            className="max-h-60 overflow-y-auto text-gray-900 bg-white rounded-lg"
            onScroll={handleScroll}
          >
            {filteredOptions.length === 0 && !isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No options found
              </div>
            ) : (
              <>
                {filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "relative flex w-full select-none items-center rounded-sm py-3 pl-8 pr-2 text-sm outline-none cursor-pointer",
                      "hover:bg-gray-100 hover:text-accent-foreground transition-colors",
                      value === option.value && "bg-gray-100 font-medium"
                    )}
                  >
                    <span className="absolute left-3 flex h-3.5 w-3.5 items-center justify-center">
                      {value === option.value && <Check className="h-4 w-4" />}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </button>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading more...
                    </span>
                  </div>
                )}

                {/* End of list message */}
                {!hasMore && filteredOptions.length > 0 && (
                  <div className="py-2 text-center text-xs text-muted-foreground border-t">
                    No more option
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
